import React, { useState, useEffect, useCallback } from 'react';
import styles from './Team.module.css';
import AxiosUrl from '../AxiosUrl';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Same list as the backend (utils/teamPermissions.js); the page loads the
// live list from /team/permissions and uses this only until it arrives.
const FALLBACK_PERMISSIONS = [
    { key: 'orders.edit', label: 'Edit orders' },
    { key: 'orders.status', label: 'Change order status' },
    { key: 'orders.delete', label: 'Delete orders' },
    { key: 'orders.courier', label: 'Send to Steadfast courier' },
    { key: 'orders.sms', label: 'Send SMS to customers' },
    { key: 'orders.call', label: 'AI calls and softphone' },
    { key: 'returns', label: 'Receive returned parcels' },
];
// New members: everything except deleting orders and sending SMS.
const NEW_MEMBER_DEFAULT = ['orders.edit', 'orders.status', 'orders.courier', 'orders.call', 'returns'];

const tk = (n) => `৳${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

function usePermissionList() {
    const [list, setList] = useState(FALLBACK_PERMISSIONS);
    useEffect(() => {
        AxiosUrl.get('/team/permissions')
            .then((res) => Array.isArray(res.data?.permissions) && res.data.permissions.length && setList(res.data.permissions))
            .catch(() => {});
    }, []);
    return list;
}

/** Permissions, which orders they see, and the monthly target. */
function AccessFields({ value, onChange }) {
    const list = usePermissionList();
    const set = (patch) => onChange({ ...value, ...patch });
    const toggle = (key) =>
        set({ permissions: value.permissions.includes(key) ? value.permissions.filter((k) => k !== key) : [...value.permissions, key] });
    return (
        <>
            <div className={styles.formGroup}>
                <label>What can this member do?</label>
                <div className={styles.permHint}>Every member can see the order list. Tick what else they may do.</div>
                <div className={styles.permGrid}>
                    {list.map((p) => (
                        <label key={p.key} className={styles.permItem}>
                            <input type="checkbox" checked={value.permissions.includes(p.key)} onChange={() => toggle(p.key)} />
                            <span>{p.label}</span>
                        </label>
                    ))}
                </div>
            </div>
            <div className={styles.formGroup}>
                <label>Which orders can they see?</label>
                <div className={styles.scopeRow}>
                    <label className={styles.permItem}>
                        <input type="radio" name="orderScope" checked={value.orderScope !== 'assigned'} onChange={() => set({ orderScope: 'all' })} />
                        <span>All orders</span>
                    </label>
                    <label className={styles.permItem}>
                        <input type="radio" name="orderScope" checked={value.orderScope === 'assigned'} onChange={() => set({ orderScope: 'assigned' })} />
                        <span>Only orders given to them</span>
                    </label>
                </div>
            </div>
            <div className={styles.twoCol}>
                <div className={styles.formGroup}>
                    <label>Monthly sales target (৳)</label>
                    <input type="number" min="0" inputMode="numeric" placeholder="e.g. 150000" value={value.monthlyTarget || ''} onChange={(e) => set({ monthlyTarget: e.target.value })} />
                </div>
                <div className={styles.formGroup}>
                    <label>Monthly confirmed orders</label>
                    <input type="number" min="0" inputMode="numeric" placeholder="e.g. 120" value={value.monthlyOrderTarget || ''} onChange={(e) => set({ monthlyOrderTarget: e.target.value })} />
                </div>
            </div>
        </>
    );
}

/**
 * Add Team Modal - Inline Component
 */
function AddTeamModal({ setOpenAddModal }) {
    const [name, setName] = useState('');
    const [mobile, setMobile] = useState('');
    const [password, setPassword] = useState('');
    const [access, setAccess] = useState({ permissions: NEW_MEMBER_DEFAULT, orderScope: 'all', monthlyTarget: '', monthlyOrderTarget: '' });
    const [loading, setLoading] = useState(false);
    const uid = localStorage.getItem('id');

    const submitTeam = () => {
        setLoading(true);
        if (!name || !mobile || !password) {
            toast.error('Please fill all fields');
            setLoading(false);
            return;
        }

        AxiosUrl.post(`/add-team/${uid}`, { uid, name, mobile, password, ...access })
            .then((data) => {
                if (data.data === 'Create_team success..') {
                    toast.success('Team member added successfully');
                    setOpenAddModal(false);
                } else {
                    toast.error(data.data);
                }
                setLoading(false);
            })
            .catch(() => {
                toast.error('Failed to add team member');
                setLoading(false);
            });
    };

    return (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && setOpenAddModal(false)}>
            <div className={styles.modal}>
                <div className={styles.modalBody}>
                    <button className={styles.closeButton} onClick={() => setOpenAddModal(false)}>
                        ×
                    </button>

                    <div className={styles.formGroup}>
                        <label>Name</label>
                        <input type="text" placeholder="Enter name" onChange={(e) => setName(e.target.value)} value={name} />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Mobile</label>
                        <input type="text" placeholder="01700000000" onChange={(e) => setMobile(e.target.value)} value={mobile} />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Password</label>
                        <input type="password" placeholder="••••••••••" onChange={(e) => setPassword(e.target.value)} value={password} />
                    </div>

                    <AccessFields value={access} onChange={setAccess} />
                </div>

                <div className={styles.modalFooter}>
                    <button className={styles.cancelButton} onClick={() => setOpenAddModal(false)}>
                        Cancel
                    </button>
                    <button className={styles.submitButton} onClick={submitTeam} disabled={loading}>
                        {loading ? 'Loading...' : 'Continue'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Edit Team Modal - Inline Component
 */
function EditTeamModal({ setOpenEditModal, data }) {
    const [name, setName] = useState(data.name);
    const [mobile, setMobile] = useState(data.mobile);
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [userStatus, setStatus] = useState(data.userStatus);
    const [access, setAccess] = useState({
        permissions: Array.isArray(data.permissions) ? data.permissions : FALLBACK_PERMISSIONS.map((p) => p.key),
        orderScope: data.orderScope || 'all',
        monthlyTarget: data.monthlyTarget || '',
        monthlyOrderTarget: data.monthlyOrderTarget || '',
    });

    const uid = localStorage.getItem('id');

    const submitTeamEdit = () => {
        setLoading(true);
        AxiosUrl.post(`/edit-team/${uid}`, { id: data._id, name, mobile, password, userStatus, ...access })
            .then((res) => {
                toast.success(res.data);
                setOpenEditModal(false);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    return (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && setOpenEditModal(false)}>
            <div className={styles.modal}>
                <div className={styles.modalBody}>
                    <button className={styles.closeButton} onClick={() => setOpenEditModal(false)}>
                        ×
                    </button>

                    <div className={styles.formGroup}>
                        <label>Name</label>
                        <input type="text" placeholder="Enter name" onChange={(e) => setName(e.target.value)} value={name} />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Mobile</label>
                        <input type="text" placeholder="01700000000" onChange={(e) => setMobile(e.target.value)} value={mobile} />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Password</label>
                        <input type="password" placeholder="Leave blank to keep current password" onChange={(e) => setPassword(e.target.value)} value={password} />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Status</label>
                        <select name="status" onChange={(e) => setStatus(e.target.value)} value={userStatus}>
                            <option value="0">Active</option>
                            <option value="1">Inactive</option>
                        </select>
                    </div>

                    <AccessFields value={access} onChange={setAccess} />
                </div>

                <div className={styles.modalFooter}>
                    <button className={styles.cancelButton} onClick={() => setOpenEditModal(false)}>
                        Cancel
                    </button>
                    <button className={styles.submitButton} onClick={submitTeamEdit} disabled={loading}>
                        {loading ? 'Loading...' : 'Update'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function monthOptions() {
    const out = [];
    const d = new Date();
    for (let i = 0; i < 12; i++) {
        const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
        out.push({
            value: `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`,
            label: i === 0 ? 'This month' : i === 1 ? 'Last month' : x.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
        });
    }
    return out;
}

function Progress({ pct, passed }) {
    if (pct === null || pct === undefined) return <span className={styles.muted}>No target</span>;
    const w = Math.min(100, Math.max(0, pct));
    const tone = pct >= 100 ? styles.barDone : passed && pct < passed * 0.8 ? styles.barBehind : styles.barOn;
    return (
        <div className={styles.progressWrap}>
            <div className={styles.progress}>
                <div className={`${styles.bar} ${tone}`} style={{ width: `${w}%` }} />
            </div>
            <span className={styles.pct}>{pct}%</span>
        </div>
    );
}

/** Team → Performance: orders per member for a month, against their target. */
function Performance() {
    const months = monthOptions();
    const [month, setMonth] = useState(months[0].value);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        AxiosUrl.get(`/team-performance?month=${month}`)
            .then((res) => setData(res.data))
            .catch(() => toast.error('Could not load team performance'))
            .finally(() => setLoading(false));
    }, [month]);
    useEffect(() => {
        load();
    }, [load]);

    const isThisMonth = month === months[0].value;
    const rows = [...(data?.members || []), ...(data?.others || []).filter((r) => r.orders > 0)];
    const totals = rows.reduce(
        (t, r) => ({ orders: t.orders + r.orders, confirmed: t.confirmed + r.confirmed, value: t.value + r.confirmedValue }),
        { orders: 0, confirmed: 0, value: 0 }
    );
    return (
        <div className={styles.perf}>
            <div className={styles.perfBar}>
                <div className={styles.perfNote}>
                    Orders are given to active members in turn when they come in. “Confirmed” = Call Done onwards (not cancelled or returned). Sales = product amount minus discount.
                    {isThisMonth && data ? ` ${data.monthPassed}% of the month has passed.` : ''}
                </div>
                <select className={styles.monthSelect} value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month">
                    {months.map((m) => (
                        <option key={m.value} value={m.value}>
                            {m.label}
                        </option>
                    ))}
                </select>
            </div>
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Member</th>
                            <th>Orders</th>
                            <th>Confirmed</th>
                            <th>Cancelled</th>
                            <th>Returned</th>
                            <th>Pending</th>
                            <th>Sales</th>
                            <th>Sales target</th>
                            <th>Orders target</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="9" className={styles.emptyCell}>
                                    Loading…
                                </td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan="9" className={styles.emptyCell}>
                                    No orders this month.
                                </td>
                            </tr>
                        ) : (
                            rows.map((r) => (
                                <tr key={r.id} className={r.removed ? styles.rowMuted : ''}>
                                    <td>
                                        <strong>{r.name}</strong>
                                        {r.active === false && <span className={styles.muted}> (inactive)</span>}
                                    </td>
                                    <td>{r.orders}</td>
                                    <td>
                                        {r.confirmed}
                                        {r.confirmRate !== null && <span className={styles.muted}> ({r.confirmRate}%)</span>}
                                    </td>
                                    <td>{r.cancelled}</td>
                                    <td>{r.returned}</td>
                                    <td>{r.pending}</td>
                                    <td>
                                        <strong>{tk(r.confirmedValue)}</strong>
                                    </td>
                                    <td>
                                        {r.removed ? (
                                            '—'
                                        ) : (
                                            <>
                                                <Progress pct={r.targetPct} passed={isThisMonth ? data?.monthPassed : 100} />
                                                {r.target > 0 && <div className={styles.muted}>of {tk(r.target)}</div>}
                                            </>
                                        )}
                                    </td>
                                    <td>
                                        {r.removed ? (
                                            '—'
                                        ) : (
                                            <>
                                                <Progress pct={r.orderTargetPct} passed={isThisMonth ? data?.monthPassed : 100} />
                                                {r.orderTarget > 0 && <div className={styles.muted}>of {r.orderTarget} orders</div>}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    {!loading && rows.length > 0 && (
                        <tfoot>
                            <tr>
                                <td>
                                    <strong>Total</strong>
                                </td>
                                <td>{totals.orders}</td>
                                <td>{totals.confirmed}</td>
                                <td colSpan="3"></td>
                                <td>
                                    <strong>{tk(totals.value)}</strong>
                                </td>
                                <td colSpan="2"></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
}

function accessSummary(m, total) {
    const n = Array.isArray(m.permissions) ? m.permissions.length : total;
    return `${n} of ${total} · ${m.orderScope === 'assigned' ? 'own orders' : 'all orders'}`;
}

/**
 * Team - Team Members Management
 * Design: Shopify Polaris-inspired (matching DNS list design)
 */
function Team() {
    const uid = localStorage.getItem('id');
    const [tab, setTab] = useState('members');
    const [teamMembers, setTeamMembers] = useState([]);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [editMember, setEditMember] = useState('');
    const [loading, setLoading] = useState(true);
    const [refresh, setRefresh] = useState(false);

    useEffect(() => {
        fetchTeamMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openEditModal, refresh, openAddModal]);

    const fetchTeamMembers = () => {
        setLoading(true);
        AxiosUrl.get(`/get-team/${uid}`)
            .then((res) => {
                setTeamMembers(Array.isArray(res.data) ? res.data : []);
                setLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setLoading(false);
            });
    };

    const deleteTeamMember = (mobile) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this team member?');
        if (confirmDelete) {
            setLoading(true);
            setRefresh(false);
            AxiosUrl.post(`/delete-team/${uid}`, { mobile })
                .then((res) => {
                    setLoading(false);
                    if (res.data === 'Delete succes..') {
                        toast.success('Team member deleted successfully');
                        setRefresh(true);
                    }
                })
                .catch(() => {
                    toast.error('Failed to delete the team member.');
                    setLoading(false);
                });
        }
    };

    const copyLinkToClipboard = () => {
        const loginLink = 'https://seller.ecomtechbd.com/teamlogin';
        navigator.clipboard.writeText(loginLink);
        toast.success('Login link copied to clipboard!');
    };

    return (
        <div className={styles.container}>
            <ToastContainer />
            {openEditModal && <EditTeamModal setOpenEditModal={setOpenEditModal} data={editMember} />}
            {openAddModal && <AddTeamModal setOpenAddModal={setOpenAddModal} />}

            {/* Header */}
            <div className={styles.header}>
                <div>
                    <h1>Team Management</h1>
                    <p>Manage your team members, what they can do, and their monthly targets</p>
                </div>
                <div className={styles.headerActions}>
                    <button className={styles.copyLinkButton} onClick={copyLinkToClipboard}>
                        <i className="fas fa-link"></i>
                        Copy Login Link
                    </button>
                    <button className={styles.addButton} onClick={() => setOpenAddModal(true)}>
                        <i className="fas fa-plus"></i>
                        Add Member
                    </button>
                </div>
            </div>

            <div className={styles.tabs} role="tablist">
                <button type="button" role="tab" aria-selected={tab === 'members'} className={tab === 'members' ? styles.tabOn : styles.tab} onClick={() => setTab('members')}>
                    Members
                </button>
                <button type="button" role="tab" aria-selected={tab === 'performance'} className={tab === 'performance' ? styles.tabOn : styles.tab} onClick={() => setTab('performance')}>
                    Performance
                </button>
            </div>

            {tab === 'performance' ? (
                <Performance />
            ) : (
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Mobile Number</th>
                                <th>Access</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <>
                                    {[1, 2, 3].map((i) => (
                                        <tr key={`skel-${i}`} className={styles.skeletonRow}>
                                            <td>
                                                <div className={styles.skeleton} style={{ width: '120px', height: '16px' }}></div>
                                            </td>
                                            <td>
                                                <div className={styles.skeleton} style={{ width: '110px', height: '16px' }}></div>
                                            </td>
                                            <td>
                                                <div className={styles.skeleton} style={{ width: '120px', height: '16px' }}></div>
                                            </td>
                                            <td>
                                                <div className={styles.skeleton} style={{ width: '70px', height: '24px', borderRadius: '12px' }}></div>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                    <div className={styles.skeleton} style={{ width: '65px', height: '32px', borderRadius: '6px' }}></div>
                                                    <div className={styles.skeleton} style={{ width: '75px', height: '32px', borderRadius: '6px' }}></div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </>
                            ) : teamMembers.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className={styles.emptyCell}>
                                        <div className={styles.emptyIcon}>
                                            <i className="fas fa-users"></i>
                                        </div>
                                        <h3>No Team Members</h3>
                                        <p>Click "Add Member" to invite your first team member</p>
                                    </td>
                                </tr>
                            ) : (
                                teamMembers.map((data) => (
                                    <tr key={data._id}>
                                        <td>
                                            <strong>{data.name}</strong>
                                        </td>
                                        <td>{data.mobile}</td>
                                        <td className={styles.muted}>{accessSummary(data, FALLBACK_PERMISSIONS.length)}</td>
                                        <td>
                                            <span className={data.userStatus === 0 ? styles.statusActive : styles.statusInactive}>
                                                {data.userStatus === 0 ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.actionBtns}>
                                                <button
                                                    className={styles.editBtn}
                                                    onClick={() => {
                                                        setEditMember(data);
                                                        setOpenEditModal(true);
                                                    }}
                                                >
                                                    <i className="fas fa-edit"></i>
                                                    Edit
                                                </button>
                                                <button className={styles.deleteBtn} onClick={() => deleteTeamMember(data.mobile)} disabled={loading}>
                                                    <i className="fas fa-trash"></i>
                                                    {loading ? 'Deleting...' : 'Delete'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default Team;
